import React, { useEffect, useMemo, useState } from 'react';
import { __, _n, sprintf } from '@wordpress/i18n';
import { useForm, useWatch } from 'react-hook-form';

// store
import { storeSet, useStoreValue } from '../../store';
import { historyStore } from '../../store/historyStore';

// helpers
import { getCueSheetStatusOptions, getCueSheetStatusLabel } from '../../helpers/cueSheetStatuses';

// services
import { ASSIGN_NOTE_MAX_LENGTH } from '../../service/permissions-service';

// components
import FormField from '../FormField';
import ButtonFlat from '../ButtonWrapper/components/ButtonFlat';
import ButtonFormSubmit from '../ButtonWrapper/components/ButtonFormSubmit';
import ModalDialog from '../ModalDialog';
import UserAvatar from '../UserAvatar';

const STATUS_OPTIONS = getCueSheetStatusOptions(__('Lascia invariato', 'soundbetween'));

/** The ids a group of cues shares, or null when they do not all agree. */
const commonAssigneeIds = (items) => {
    const signatures = new Set();
    let first = null;

    items.forEach(item => {
        const ids = [...(item.assigneeUserIds
            ?? (item.assignedUserId ? [item.assignedUserId] : []))].filter(Boolean).sort();

        signatures.add(ids.join('|'));

        if (first === null) first = ids;
    });

    return signatures.size === 1 ? first : null;
};

/**
 * "Modifica" on a multiple selection: status and assignees.
 *
 * Title, label and timecode identify one cue each and writing the same value
 * over a group would destroy them, so they stay out.
 *
 * The two fields travel on their own endpoint. The status goes through
 * `POST .../cue-sheet/bulk-with-history` — the store action collects the
 * updates and the history layer sends them together — so one undo takes the
 * group back. The assignees go through `POST .../cue-sheet/assignees/bulk`,
 * which is a replace-set per cue and notifies each new assignee once.
 *
 * Assignees are written first: the backend moves a cue to `assigned` (or back
 * to `to_work`) on its own when the list changes, and an explicit status has to
 * be the one that survives.
 */
const ModalBulkEditCueSheetItems = ({ visible, onHide, projectId, items = [] }) => {
    const [loading, setLoading] = useState(false);
    const team = useStoreValue('team');
    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid },
    } = useForm({
        mode: 'onChange',
        defaultValues: { status: '', assignees: [], note: '' },
    });

    const status = useWatch({ control, name: 'status' }) ?? '';
    const note = useWatch({ control, name: 'note' }) ?? '';
    const assignees = useWatch({ control, name: 'assignees' }) ?? [];

    // Null when the selection does not share one set of assignees: the field
    // then opens empty and says so, because saving it would level the group.
    const sharedAssignees = useMemo(() => commonAssigneeIds(items), [items]);

    useEffect(() => {
        if (!visible) return;

        // A group already sharing one status opens on it; a mixed one opens empty.
        const statuses = [...new Set(items.map(item => item.status).filter(Boolean))];

        reset({
            status: statuses.length === 1 ? statuses[0] : '',
            assignees: sharedAssignees ?? [],
            note: '',
        });
    }, [visible, items, sharedAssignees, reset]);

    const teamOptions = useMemo(() => team.map(member => ({
        ...member,
        value: member.userId,
        label: `${member.name || ''} ${member.surname || ''}`.trim() || member.invitedEmail,
    })), [team]);

    const renderSelectedUser = (user, handleRemove) => (
        <>
            <UserAvatar url={user.profile_picture_url} name={user.label}/>
            <span className="multiSelectDropdown__selectedItemLabel">{user.label}</span>
            <button
                type="button"
                onClick={() => handleRemove(user.value)}
                className="multiSelectDropdown__removeBtn"
                aria-label={__('Rimuovi utente', 'soundbetween')}
            />
        </>
    );

    /**
     * Whether the assignee field is asking for a change. It is answered by
     * comparing the two lists rather than by the dirty flag: an empty field on
     * a group that does not share its assignees means "leave each cue alone",
     * not "unassign everybody", and only a diff can tell the two apart.
     */
    const assigneesTouched = useMemo(() => {
        const before = [...(sharedAssignees ?? [])].sort().join('|');
        const after = [...assignees].sort().join('|');

        return before !== after;
    }, [sharedAssignees, assignees]);

    const hasChanges = Boolean(status) || assigneesTouched;

    const onSubmit = async (formData) => {
        if (!hasChanges) return;

        setLoading(true);

        try {
            if (assigneesTouched) {
                await storeSet(
                    'bulkSetCueSheetAssignees',
                    projectId,
                    items.map(item => item.id),
                    formData.assignees ?? [],
                    (formData.note ?? '').trim()
                );
            }

            if (formData.status) {
                historyStore.getState().beginAction(__('Cambia stato', 'soundbetween'));
                await storeSet(
                    'saveCueSheetUpdates',
                    projectId,
                    items.map(item => ({ id: item.id, status: formData.status }))
                );
            }

            onHide();
        } catch (error) {
            // Error already displayed by the store action
        } finally {
            setLoading(false);
        }
    };

    if (!visible || items.length === 0) return null;

    return (
        <ModalDialog
            additionalClasses={['modalEditCueSheetItem']}
            header={sprintf(
                _n('Modifica %d elemento', 'Modifica %d elementi', items.length, 'soundbetween'),
                items.length
            )}
            visible={visible}
            onSubmit={handleSubmit(onSubmit)}
            onHide={onHide}>
            <form className="appForm formInModal" onSubmit={handleSubmit(onSubmit)}>
                <ul className="bulkEditSummary">
                    {items.map(item => (
                        <li key={item.id} className="bulkEditSummary__item">
                            <span className="bulkEditSummary__label">{item.label || item.title}</span>
                            <span className="bulkEditSummary__status">
                                {getCueSheetStatusLabel(item.status)}
                            </span>
                        </li>
                    ))}
                </ul>

                <FormField
                    control={control}
                    name="status"
                    label={__('Stato', 'soundbetween')}
                    type="select"
                    options={STATUS_OPTIONS}
                />

                <FormField
                    type="multiselect"
                    name="assignees"
                    label={__('Assegnatari', 'soundbetween')}
                    control={control}
                    initialOptions={teamOptions}
                    labelKey="label"
                    valueKey="value"
                    placeholder={__('Cerca membri...', 'soundbetween')}
                    displayMode="count"
                    showClearAll={true}
                    renderSelectedItem={renderSelectedUser}
                />

                {sharedAssignees === null
                    ? <p className="appFormNotice">
                        {__(
                            'Le cue selezionate hanno assegnatari diversi: lasciando il campo vuoto restano come sono, scegliendo dei membri la lista scelta sostituisce la loro.',
                            'soundbetween'
                        )}
                    </p>
                    : null}

                {assigneesTouched && assignees.length === 0
                    ? <p className="appFormNotice">
                        {__('Salvando senza membri selezionati le cue tornano “Da lavorare”.', 'soundbetween')}
                    </p>
                    : null}

                {assigneesTouched
                    ? <FormField
                        type="textarea"
                        name="note"
                        rows={4}
                        maxLength={ASSIGN_NOTE_MAX_LENGTH}
                        label={__('Nota per gli assegnatari', 'soundbetween')}
                        control={control}
                        description={`${note.length}/${ASSIGN_NOTE_MAX_LENGTH}`}
                        rules={{
                            maxLength: {
                                value: ASSIGN_NOTE_MAX_LENGTH,
                                message: __('La nota non può superare 1024 caratteri', 'soundbetween'),
                            },
                        }}
                    />
                    : null}

                <div className="appFormActions">
                    <ButtonFlat size="lg" type="button" onClick={onHide}>
                        {__('Annulla', 'soundbetween')}
                    </ButtonFlat>
                    <ButtonFormSubmit disabled={loading || !isValid || !hasChanges}>
                        {__('Salva', 'soundbetween')}
                    </ButtonFormSubmit>
                </div>
            </form>
        </ModalDialog>
    );
};

export default ModalBulkEditCueSheetItems;
